import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, money } from "../lib/api";
import { CelestialMap } from "../components/CelestialMap";
import { OrderSummary } from "../components/OrderSummary";
import { padNum } from "../components/NumberPicker";
import { logError } from "../lib/log";

const loadCashfree = () =>
  new Promise((resolve) => {
    if (window.Cashfree) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default function Checkout() {
  const { registryId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [paying, setPaying] = useState(false);
  const [promo, setPromo] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/registries/${registryId}`);
      setRecord(data);
      return data;
    } catch (error) {
      logError("Could not load registry record", error);
      toast.error("Registry record not found");
      navigate("/dashboard");
      return null;
    }
  }, [registryId, navigate]);

  // Returning from the Cashfree page proves nothing — the server decides the real status.
  const settleFromServer = useCallback(async (silent) => {
    try {
      const { data } = await api.get(`/payments/status/${registryId}`);
      if (data.status === "PAID") {
        toast.success("Payment confirmed — registry record issued");
        navigate(`/registry/${registryId}`);
        return true;
      }
      if (!silent) {
        const messages = {
          FAILED: "The payment failed. Your registry number is still held — you can try again.",
          USER_DROPPED: "The payment was cancelled. Your registry number is still held.",
          CANCELLED: "The payment was cancelled. Your registry number is still held.",
          EXPIRED: "That payment session expired. Please start the payment again.",
          PENDING: "The payment is still being confirmed. Please wait a moment and refresh.",
        };
        toast.error(messages[data.status] || "Payment was not completed.");
      }
    } catch (error) {
      if (!silent) toast.error(error.response?.data?.detail || "Could not confirm the payment status");
    }
    return false;
  }, [registryId, navigate]);

  useEffect(() => {
    load().then((data) => {
      if (data && data.payment_status !== "PAID" && params.get("cf_order_id")) settleFromServer(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const applyPromo = async () => {
    if (!promo.trim()) return;
    try {
      const { data } = await api.patch(`/registries/${registryId}/promo`, { code: promo, amount: record.base_amount });
      setRecord(data);
      setPromo("");
      toast.success(`Code ${data.promotion_code} applied — ${money(data.discount)} off`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid code");
    }
  };

  const openCashfree = async (order) => {
    const ok = await loadCashfree();
    if (!ok) { toast.error("Could not load the payment gateway"); return; }
    const cashfree = window.Cashfree({
      mode: order.environment === "PRODUCTION" ? "production" : "sandbox",
    });
    const result = await cashfree.checkout({
      paymentSessionId: order.payment_session_id,
      redirectTarget: "_modal",
    });
    if (result?.error) {
      await settleFromServer(true);
      toast.error(result.error.message || "Payment was not completed");
      return;
    }
    await settleFromServer(false);
  };

  const pay = async () => {
    setPaying(true);
    try {
      const { data: order } = await api.post(`/payments/order/${registryId}`);
      if (order.mode === "demo") {
        const { data } = await api.post("/payments/verify", { registry_id: registryId });
        toast.success("Payment simulated (test mode) — registry record confirmed");
        navigate(`/registry/${data.registry_id}`);
        return;
      }
      await openCashfree(order);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not start payment");
    } finally {
      setPaying(false);
    }
  };

  if (!record) return <div className="py-40 text-center"><Loader2 className="w-6 h-6 animate-spin text-amber-300 mx-auto" /></div>;

  if (record.payment_status === "PAID")
    return (
      <div className="max-w-3xl mx-auto px-5 py-32 text-center" data-testid="checkout-paid">
        <h1 className="font-display text-3xl text-amber-200">This record is already confirmed</h1>
        <Link to={`/registry/${record.registry_id}`} className="hy-btn inline-block px-8 py-4 text-xs uppercase mt-8">View Registry Record</Link>
      </div>
    );

  return (
    <div className="max-w-6xl mx-auto px-5 lg:px-10 py-24" data-testid="checkout-page">
      <div className="hy-label">Checkout</div>
      <h1 className="font-display text-4xl sm:text-5xl text-white mt-5">Confirm your registration</h1>

      <div className="grid lg:grid-cols-3 gap-10 mt-14">
        <div className="lg:col-span-2 space-y-8">
          <div className="hy-card p-8" data-testid="checkout-record">
            <div className="flex flex-wrap items-baseline justify-between gap-4">
              <div className="font-display text-2xl text-amber-200" data-testid="checkout-number">
                HYLUNER REGISTRY {padNum(record.registry_number)}
              </div>
              <div className="hy-label">{record.edition} · {record.registry_type}</div>
            </div>
            {!record.number_confirmed && (
              <p className="text-xs text-slate-500 mt-3" data-testid="checkout-reserved-note">
                {padNum(record.registry_number)} is reserved for you and becomes permanently yours once
                payment is confirmed. Your quoted price is locked.
              </p>
            )}
            <div className="hy-rule my-7" />
            <div className="grid sm:grid-cols-2 gap-6 text-sm">
              <Row k="Dedication" v={record.dedication_name} />
              <Row k="Recipient" v={record.recipient_name} />
              <Row k="Registry ID" v={record.registry_id} />
              <Row k="Celestial Object" v={record.celestial_object_id} />
              <Row k="Location" v={record.location_display} />
              <Row k="Purchase Type" v={record.purchase_type} />
              {record.delivery_address && (
                <div className="sm:col-span-2" data-testid="checkout-delivery-address">
                  <div className="hy-label text-[0.55rem]">Framed certificate delivery</div>
                  <div className="text-slate-200 mt-1 break-words">
                    {[record.delivery_address.full_name, record.delivery_address.line1,
                      record.delivery_address.line2, record.delivery_address.city,
                      record.delivery_address.state, record.delivery_address.postal_code,
                      record.delivery_address.country].filter(Boolean).join(", ")}
                  </div>
                </div>
              )}
            </div>
          </div>
          <CelestialMap record={record} />
        </div>

        <OrderSummary record={record} promo={promo} setPromo={setPromo}
          onApplyPromo={applyPromo} onPay={pay} paying={paying} />
      </div>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div>
    <div className="hy-label text-[0.55rem]">{k}</div>
    <div className="text-slate-200 mt-1 break-words">{v}</div>
  </div>
);
